export const getTypes = (t, hasZapier) => {
  const opts = [
    { label: `Create a card when a ${t('Customer').toLowerCase()} is created`, value: 'customer' },
    { label: `Create a card when a ${t('Project').toLowerCase()} is created`, value: 'project' },
    { label: `Create a card when a ${t('Project').toLowerCase()} type is changed`, value: 'projectTypeChange' },
    { label: 'Create a card when a user is created', value: 'user' },
    { label: 'Create a card when a material is created', value: 'material' },
    { label: 'Create a card when equipment is created', value: 'equipment' },
    { label: 'Create tasks on status change', value: 'statusTasks' },
    { label: 'Copy a card on status change', value: 'copyCard' },
    { label: 'Move card between boards', value: 'moveBoard' },
    { label: 'Trigger action when a card stays in a status for a period of time', value: 'statusTime', hasSubType: true },
    { label: 'Trigger action when a card is not in a status within a period of time', value: 'notInStatus', hasSubType: true },
    { label: 'Create a customer and/or project when a card is created', value: 'createCustomerProject' },
    { label: 'Create a customer and/or project on status change', value: 'statusCustomerProject' },
  ];
  if (hasZapier) {
    opts.push({ label: 'Trigger Zapier when a card moves into a status', value: 'zapierMove' });
  }
  return opts;
};

const TIME_TRIGGER_SUB_TYPES = [
  { label: 'Send an email', value: 'sendEmail' },
  { label: 'Create a task', value: 'createTask' },
  { label: 'Change card color', value: 'changeCardColor' },
];

export const SUB_TYPE_MAP = {
  statusTime: TIME_TRIGGER_SUB_TYPES,
  notInStatus: TIME_TRIGGER_SUB_TYPES,
};

/**
 * Workflow types that trigger based on how long a card has/has not been in a status.
 * These share the same sub-types, timing (duration or card date field) and action config.
 */
export const TIME_TRIGGER_TYPES = ['statusTime', 'notInStatus'];

export const isTimeTriggerType = (type) => TIME_TRIGGER_TYPES.includes(type);

/** Field types on a card design that can be used as a dynamic trigger date */
export const DATE_FIELD_TYPES = ['dateTime', 'dateRange'];

const CUSTOMER_FIELDS = [
  {
    label: 'Name', value: 'name', type: 'text', required: true,
  },
  {
    label: 'Office Phone Number', value: 'officeNumber', type: 'text', required: true,
  },
  { label: 'Mobile Phone Number', value: 'mobileNumber', type: 'text' },
  { label: 'Fax Phone Number', value: 'faxNumber', type: 'text' },
  {
    label: 'Office Email', value: 'officeEmail', type: 'text', required: true,
  },
  { label: 'Billing Email', value: 'billingEmail', type: 'text' },
  { label: 'Website', value: 'website', type: 'text' },
  { label: 'Office Address', value: 'officeAddress', type: 'text' },
  { label: 'Billing Address', value: 'billingAddress', type: 'text' },
  { label: 'Info', value: 'info', type: 'text' },
];

const PROJECT_FIELDS = [
  {
    label: 'Name', value: 'name', type: 'text', required: true,
  },
  {
    label: 'Number', value: 'number', type: 'text', required: true,
  },
  {
    label: 'Address', value: 'address', type: 'text', required: true,
  },
  {
    label: 'Labour Hours', value: 'labourHours', type: 'text', required: true,
  },
  {
    label: 'Labour Cost', value: 'labourCost', type: 'text', required: true,
  },
  {
    label: 'Material Cost', value: 'estimatedMaterialCost', type: 'text', required: true,
  },
  {
    label: 'Overhead Cost', value: 'estimatedOverheadCost', type: 'text', required: true,
  },
  {
    label: 'Contract Value', value: 'contractValue', type: 'text', required: true,
  },
  {
    label: 'Material Discount/Markup', value: 'materialDiscount', type: 'text', required: true,
  },
  {
    label: 'Billing Contact Email', value: 'billingContact', type: 'text', required: true,
  },
  {
    label: 'PO Number', value: 'poNumber', type: 'text', required: true,
  },
  {
    label: 'Info', value: 'info', type: 'text', required: true,
  },
  {
    label: 'Holdback Percent', value: 'holdbackPercentage', type: 'text', required: true,
  },
];

const getProjectTypeFields = (t) => (
  PROJECT_FIELDS.concat(
    [
      {
        label: t('Customer'), value: 'customerId', type: 'dropdown', dataType: 'Customers',
      },
    ],
    CUSTOMER_FIELDS.map((field) => ({
      ...field,
      label: `${t('Customer')} - ${field.label}`,
      value: `customer - ${field.value}`,
      isCustomer: true,
    })),
    [
      {
        label: t('Project'), value: 'id', type: 'dropdown', dataType: 'Projects',
      },
    ],
  )
);
export const getTypeMap = (t) => ({
  customer: CUSTOMER_FIELDS,
  project: getProjectTypeFields(t),
  projectTypeChange: getProjectTypeFields(t),
  user: [
    {
      label: 'Name', value: 'name', type: 'text', required: true,
    },
    {
      label: 'Employee ID', value: 'employeeId', type: 'text', required: true,
    },
    {
      label: 'Email', value: 'email', type: 'text', required: true,
    },
    {
      label: 'Username', value: 'username', type: 'text', required: true,
    },
    {
      label: 'Phone Number', value: 'phoneNumber', type: 'text', required: true,
    },
    {
      label: 'Wage', value: 'wage', type: 'text', required: true,
    },
  ],
  material: [
    {
      label: 'Name', value: 'name', type: 'text', required: true,
    },
    {
      label: 'Description', value: 'description', type: 'text', required: true,
    },
    {
      label: 'Part Number', value: 'partNumber', type: 'text', required: true,
    },
    {
      label: 'Supplier', value: 'supplier', type: 'text', required: true,
    },
    {
      label: 'Units', value: 'units', type: 'text', required: true,
    },
    {
      label: 'Cost', value: 'cost', type: 'text', required: true,
    },
    {
      label: 'Markup', value: 'markup', type: 'text', required: true,
    },
    {
      label: 'Quantity', value: 'quantity', type: 'text', required: true,
    },
  ],
  equipment: [
    {
      label: 'Name', value: 'name', type: 'text', required: true,
    },
    {
      label: 'ID', value: 'code', type: 'text', required: true,
    },
  ],
});

export const TITLE_FIELD_ID = 'cardTitle';
export const TITLE_FIELD = {
  id: TITLE_FIELD_ID,
  configProps: { title: 'Title', required: true },
  selectedType: 'text',
};
